import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { ArrowLeft, Download, FileSpreadsheet, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { errorMessage } from "@/api/client";
import { useCancelLeadImport } from "@/api/mutations/lead-imports";
import { isImportActive, useLeadImport, useLeadImportRows } from "@/api/queries/lead-imports";
import type { LeadImport, LeadRowStatus } from "@/api/types";
import { usePermissions } from "@/auth/permissions";
import { ErrorState } from "@/components/feedback/error-state";
import { StatusBadge } from "@/components/feedback/status-badge";
import { PageHeader } from "@/components/layout/page-header";
import { TableSkeletonRows } from "@/components/tables/table-skeleton";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useStoreParam } from "@/hooks/use-store-context";
import { formatDate, formatRelative } from "@/lib/dates";
import { EMPTY, formatBytes, formatNumber, formatPhone } from "@/lib/formatters";
import { LEAD_IMPORT, LEAD_ROW, SOURCE_LABEL, TONE_CLASSES } from "@/lib/status";
import { cn } from "@/lib/utils";
import { MappingEditor } from "./mapping-editor";

export const ROW_FILTERS = ["all", "CREATED", "DUPLICATE", "FAILED"] as const;
export type RowFilter = (typeof ROW_FILTERS)[number];

export function ImportDetailPage() {
  const store = useStoreParam();
  const { importId } = useParams({ from: "/_authenticated/$store/imports/$importId" });
  const q = useLeadImport(importId);
  const { can } = usePermissions();
  const imp = q.data;

  if (q.isError) {
    return (
      <div className="p-6">
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      </div>
    );
  }
  return (
    <div className="flex flex-1 flex-col">
      <PageHeader
        title={
          <span className="flex items-center gap-2">
            {imp ? importTitle(imp) : <Skeleton className="h-5 w-56" />}
            {imp ? <StatusBadge config={LEAD_IMPORT[imp.status]} pulse={isImportActive(imp)} dot={isImportActive(imp)} /> : null}
          </span>
        }
        description={
          <Link to="/$store/imports" params={{ store }} className="inline-flex items-center gap-1 text-muted-foreground text-xs hover:text-foreground">
            <ArrowLeft className="size-3" aria-hidden /> All imports
          </Link>
        }
        actions={imp ? <Actions imp={imp} canEdit={can("opportunity:update", imp.storeId)} /> : null}
      />
      <div className="flex-1 space-y-5 overflow-auto p-6">
        {!imp ? (
          <Skeleton className="h-32" />
        ) : imp.status === "DRAFT" ? (
          <>
            <Summary imp={imp} />
            <MappingEditor imp={imp} canEdit={can("opportunity:update", imp.storeId)} />
          </>
        ) : (
          <>
            <Summary imp={imp} />
            <Results imp={imp} />
          </>
        )}
      </div>
    </div>
  );
}

export function importTitle(imp: Pick<LeadImport, "kind" | "file" | "apiKey">) {
  return imp.kind === "FILE" ? (imp.file?.name ?? "Uploaded file") : `API · ${imp.apiKey?.name ?? "integration"}`;
}

function Actions({ imp, canEdit }: { imp: LeadImport; canEdit: boolean }) {
  const cancel = useCancelLeadImport(imp.id);
  return (
    <div className="flex items-center gap-2">
      {imp.file ? (
        <Button size="sm" variant="outline" nativeButton={false} render={<a href={imp.file.url} download={imp.file.name ?? undefined} />}>
          <Download data-icon="inline-start" />
          Original file
        </Button>
      ) : null}
      {canEdit && isImportActive(imp) ? (
        <Button
          size="sm"
          variant="outline"
          disabled={cancel.isPending}
          onClick={() =>
            cancel.mutate(undefined, {
              onSuccess: () => toast.success("Import stopped", { description: "Leads already created were kept." }),
              onError: (err) => toast.error("Not stopped", { description: errorMessage(err) }),
            })
          }
        >
          Stop import
        </Button>
      ) : null}
    </div>
  );
}

function Summary({ imp }: { imp: LeadImport }) {
  const c = imp.counts;
  const tiles: Array<[string, number, keyof typeof TONE_CLASSES]> = [
    ["Created", c.created, "success"],
    ["Duplicates", c.duplicate, "info"],
    ["Failed", c.failed, "danger"],
    ["Skipped", c.skipped, "neutral"],
  ];
  return (
    <section className="space-y-4 rounded-lg border p-4" aria-label="Import summary">
      <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2 lg:grid-cols-4">
        <Item label="Store">{imp.storeName ?? EMPTY}</Item>
        <Item label="Default source">{SOURCE_LABEL[imp.defaultSource]}</Item>
        <Item label={imp.kind === "FILE" ? "Uploaded by" : "API key"}>
          {imp.kind === "FILE" ? (
            (imp.createdBy?.name ?? EMPTY)
          ) : (
            <span className="inline-flex items-center gap-1">
              <KeyRound className="size-3.5 text-muted-foreground" aria-hidden />
              {imp.apiKey?.name} <span className="font-mono text-muted-foreground text-xs">{imp.apiKey?.prefix}…</span>
            </span>
          )}
        </Item>
        <Item label="Received">
          {formatDate(imp.createdAt, { year: true })} · {formatRelative(imp.createdAt)}
        </Item>
        {imp.file ? (
          <Item label="File">
            <span className="inline-flex items-center gap-1">
              <FileSpreadsheet className="size-3.5 text-muted-foreground" aria-hidden />
              {formatBytes(imp.file.sizeBytes)}
            </span>
          </Item>
        ) : null}
        {imp.completedAt ? <Item label="Finished">{formatRelative(imp.completedAt)}</Item> : null}
      </dl>
      {imp.status !== "DRAFT" ? (
        <>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium">
                {formatNumber(c.processed)} of {formatNumber(c.total)} leads processed
              </span>
              <span className="text-muted-foreground tabular-nums">{imp.progress}%</span>
            </div>
            <Progress value={imp.progress} aria-label="Import progress" />
          </div>
          <ul className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {tiles.map(([label, n, tone]) => (
              <li key={label} className={cn("rounded-md px-3 py-2", TONE_CLASSES[tone].bg)}>
                <p className={cn("font-semibold text-lg tabular-nums", TONE_CLASSES[tone].text)}>{formatNumber(n)}</p>
                <p className="text-muted-foreground text-xs">{label}</p>
              </li>
            ))}
          </ul>
          {imp.errorMessage ? <p className="text-danger text-sm">{imp.errorMessage}</p> : null}
        </>
      ) : null}
    </section>
  );
}

function Item({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-muted-foreground text-xs">{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function Results({ imp }: { imp: LeadImport }) {
  const store = useStoreParam();
  const search = useSearch({ from: "/_authenticated/$store/imports/$importId" });
  const navigate = useNavigate();
  const filter: RowFilter = search.rows ?? "all";
  const status: LeadRowStatus[] | undefined = filter === "all" ? undefined : [filter];
  const rows = useLeadImportRows(imp.id, status, isImportActive(imp));
  const items = rows.data?.pages.flatMap((p) => p.items) ?? [];
  const counts: Record<RowFilter, number> = { all: imp.counts.total, CREATED: imp.counts.created, DUPLICATE: imp.counts.duplicate, FAILED: imp.counts.failed };
  return (
    <section aria-labelledby="rows-title" className="rounded-lg border">
      <header className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-2.5">
        <h2 id="rows-title" className="font-semibold text-sm">
          Leads
        </h2>
        <ToggleGroup
          value={[filter]}
          onValueChange={(v) => {
            const next = v[0] as RowFilter | undefined;
            if (!next) return;
            void navigate({
              to: "/$store/imports/$importId",
              params: { store, importId: imp.id },
              search: { rows: next === "all" ? undefined : next },
              replace: true,
            });
          }}
          variant="outline"
          size="sm"
          aria-label="Filter leads"
        >
          {ROW_FILTERS.map((f) => (
            <ToggleGroupItem key={f} value={f}>
              {f === "all" ? "All" : LEAD_ROW[f].label} ({formatNumber(counts[f])})
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </header>
      {rows.isError ? (
        <div className="p-4">
          <ErrorState error={rows.error} onRetry={() => void rows.refetch()} />
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="pl-4">{imp.kind === "FILE" ? "Row" : "#"}</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Seller</TableHead>
              <TableHead>Vehicle</TableHead>
              <TableHead className="pr-4">Details</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.isPending ? (
              <TableSkeletonRows rows={6} columns={5} />
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="py-8 text-center text-muted-foreground">
                  No leads here.
                </TableCell>
              </TableRow>
            ) : (
              items.map((r) => (
                <TableRow key={r.rowNumber} data-testid="import-row">
                  <TableCell className="pl-4 text-muted-foreground tabular-nums">{imp.kind === "FILE" ? r.rowNumber : r.rowNumber - 1}</TableCell>
                  <TableCell>
                    <StatusBadge config={LEAD_ROW[r.status]} dot />
                  </TableCell>
                  <TableCell>
                    <span className="block">{r.lead?.name ?? EMPTY}</span>
                    <span className="block text-muted-foreground text-xs">{r.lead?.phone ? formatPhone(r.lead.phone) : (r.lead?.email ?? "")}</span>
                  </TableCell>
                  <TableCell>{r.lead?.vehicle ?? EMPTY}</TableCell>
                  <TableCell className="pr-4 text-xs">
                    {r.opportunityId ? (
                      <Link
                        to="/$store/opportunities/$opportunityId"
                        params={{ store, opportunityId: r.opportunityId }}
                        className="mr-2 font-medium text-primary hover:underline"
                      >
                        {r.status === "DUPLICATE" ? "Existing opportunity" : "Open opportunity"}
                      </Link>
                    ) : null}
                    {r.errors.map((e) => (
                      <span key={`${e.field}-${e.message}`} className={cn("block", e.level === "error" ? "text-danger" : "text-muted-foreground")}>
                        {e.message}
                      </span>
                    ))}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      )}
      {rows.hasNextPage ? (
        <div className="border-t p-3 text-center">
          <Button size="sm" variant="outline" onClick={() => void rows.fetchNextPage()} disabled={rows.isFetchingNextPage}>
            Show more
          </Button>
        </div>
      ) : null}
    </section>
  );
}
